import { useCallback, useEffect, useState } from 'react';

// Chat "extras" that the API doesn't (yet) model as first-class data:
// pin / star / emoji reactions. We persist them per-user in localStorage so
// they survive reloads. When ecregi-api grows server-side support, swap
// these for mutation hooks — the call sites already use set-like APIs.

type StringSet = Set<string>;

function readList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
function writeList(key: string, list: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}
function readMap<T>(key: string): Record<string, T> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, T>) : {};
  } catch {
    return {};
  }
}
function writeMap<T>(key: string, map: Record<string, T>) {
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function usePinnedMessages(transactionId: string | undefined) {
  const key = transactionId ? `ecregi.chat.pinned.${transactionId}` : null;
  const [set, setSet] = useState<StringSet>(() => new Set());

  useEffect(() => {
    if (!key) return;
    setSet(new Set(readList(key)));
  }, [key]);

  const toggle = useCallback(
    (messageId: string) => {
      if (!key) return;
      setSet((prev) => {
        const next = new Set(prev);
        if (next.has(messageId)) next.delete(messageId);
        else next.add(messageId);
        writeList(key, [...next]);
        return next;
      });
    },
    [key],
  );

  return { set, toggle, ids: [...set] };
}

export function useStarredMessages() {
  const key = 'ecregi.chat.starred';
  const [set, setSet] = useState<StringSet>(() => new Set(readList(key)));

  const toggle = useCallback((messageId: string) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      writeList(key, [...next]);
      return next;
    });
  }, []);

  return { set, toggle };
}

export function useMessageReactions(transactionId: string | undefined) {
  const key = transactionId ? `ecregi.chat.reactions.${transactionId}` : null;
  const [map, setMap] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!key) return;
    setMap(readMap<string[]>(key));
  }, [key]);

  const toggle = useCallback(
    (messageId: string, emoji: string) => {
      if (!key) return;
      setMap((prev) => {
        const current = prev[messageId] ?? [];
        const has = current.includes(emoji);
        const nextList = has ? current.filter((e) => e !== emoji) : [...current, emoji];
        const next = { ...prev, [messageId]: nextList };
        if (nextList.length === 0) delete next[messageId];
        writeMap(key, next);
        return next;
      });
    },
    [key],
  );

  return { map, toggle };
}
